import { Controller } from "@hotwired/stimulus"

/**
 * Connects to data-controller="client-time"
 *
 * Re-prints a server-printed time in the BROWSER's zone when the server printed
 * it in another one (CONEK-8727). ClientTimeHelper prints /iot times in the
 * viewer's zone from the `tz` cookie (CONEK-8172), but the live regions
 * Iot::LiveRenderer broadcasts are rendered with no request and no cookie, so
 * they arrive in UTC — the gateway's once-a-minute heartbeat would flip its
 * details card back to UTC a minute after the page opened.
 *
 * The helper stamps each element with the zone it printed in (`zone`) and the
 * strftime pattern it used (`pattern`: DateFormatter's per-locale order, plus
 * " %H:%M"). Same zone as the browser → nothing happens, so a first paint keeps
 * the server's exact text and TZInfo's abbreviations. Different zone → the text
 * is rebuilt from `datetime` with numeric parts only (%d %m %Y %H %M), so there
 * are no month names to mirror. Stimulus connects every element a Turbo Stream
 * `replace` inserts, so each fragment is fixed as it lands.
 *
 * The browser zone is the value tz-detection writes the cookie from, so the two
 * agree except while a stale cookie waits for its next request.
 *
 * Two shapes:
 * - a `<time datetime>`: its text — or that of its one child, a link or a span
 *   the row wraps the text in — is re-printed. With `suffix`, a row from the
 *   other side of a DST change appends its own abbreviation (the helper's
 *   `zone_suffix:` rule: the header names today's).
 * - the abbreviation of a "Time (CEST)" header (`abbreviation`): it takes the
 *   browser zone's abbreviation for today.
 */

// Locales probed for a NAMED abbreviation: Intl names a zone only in the
// locales that use it ("CEST" in en-GB but "GMT+2" in en-US; "EDT" the other
// way round). A zone none of them names prints "UTC+04:00" — the server's rule
// for TZInfo's bare "+04".
const ABBREVIATION_LOCALES = ["en-US", "en-GB", "en-AU", "en-IN"]

const UTC_ALIASES = new Set([
  "UTC",
  "Etc/UTC",
  "Etc/UCT",
  "UCT",
  "Etc/GMT",
  "GMT",
  "Etc/Universal",
  "Universal",
  "Etc/Zulu",
  "Zulu",
])

export default class extends Controller {
  static values = {
    zone: String,
    pattern: String,
    suffix: Boolean,
    abbreviation: Boolean,
  }

  connect() {
    const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!browserZone || sameZone(browserZone, this.zoneValue)) return

    try {
      if (this.abbreviationValue) {
        this.element.textContent = zoneAbbreviation(new Date(), browserZone)
      } else {
        this._printTime(browserZone)
      }
    } catch (error) {
      // Leave the server's text: it is a true time, only in another zone.
      console.warn("client-time: could not re-print", error)
    }
  }

  _printTime(zone) {
    // A missing `datetime` must not become `new Date(null)`, the epoch.
    const iso = this.element.getAttribute("datetime")
    const date = new Date(iso)
    if (!iso || Number.isNaN(date.getTime()) || !this.patternValue) return

    let text = strftime(date, this.patternValue, zone)
    if (this.suffixValue) {
      const own = zoneAbbreviation(date, zone)
      if (own !== zoneAbbreviation(new Date(), zone)) text = `${text} ${own}`
    }
    ;(this.element.firstElementChild || this.element).textContent = text
  }
}

function sameZone(a, b) {
  return a === b || (UTC_ALIASES.has(a) && UTC_ALIASES.has(b))
}

function strftime(date, pattern, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    numberingSystem: "latn",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date)
  const part = (type) => parts.find((p) => p.type === type)?.value ?? ""
  const tokens = {
    d: part("day"),
    m: part("month"),
    Y: part("year"),
    H: part("hour"),
    M: part("minute"),
  }
  return pattern.replace(/%([dmYHM])/g, (_, token) => tokens[token])
}

function zoneAbbreviation(date, timeZone) {
  for (const locale of ABBREVIATION_LOCALES) {
    const name = zoneName(date, timeZone, locale, "short")
    if (name && !/^(GMT|UTC)[+-]/.test(name)) return name
  }
  const offset = zoneName(date, timeZone, "en-US", "longOffset")
  return offset === "GMT" ? "UTC+00:00" : offset.replace("GMT", "UTC")
}

function zoneName(date, timeZone, locale, style) {
  return new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: style })
    .formatToParts(date)
    .find((p) => p.type === "timeZoneName")?.value
}
